import React from 'react';

import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Formatters, Messages } from '~src/codegen/Localize';
import { TooltipDraftIndicator } from '~src/drafts/components/TooltipDraftIndicator';
import { ContentDraftStatus } from '~src/drafts/serverApi/types/ContentDraft';
import { getFlowDraftId } from '~src/drafts/utils/DraftUtils';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { DetailsTooltipStyles } from '~src/spacesPlatform/components/card/DetailsTooltipStyles';
import { MarkupField } from '~src/spacesPlatform/components/Explorer/widgets/MarkupField';
import { BasicField } from '~src/spacesPlatform/components/tooltip/BasicField';
import { LocationField } from '~src/spacesPlatform/components/tooltip/LocationField';
import { Name } from '~src/spacesPlatform/components/tooltip/Name';
import { Timestamp } from '~src/spacesPlatform/components/tooltip/Timestamp';
import { Tooltip } from '~src/spacesPlatform/components/tooltip/Tooltip';
import { ContentTooltipProps, getPersonalSpaceLink,getProjectLink, TooltipDescriptionTestId } from '~src/spacesPlatform/components/tooltip/TooltipHelper';
import { User } from '~src/spacesPlatform/components/tooltip/User';
import { Tags } from '~src/tags/components/tooltips/Tags';

import { editFlow } from '../../actions/EditFlowReduxAction';
import { FlowDraftItemInfo } from '../../models/FlowDraftInfo';
import { FlowBaseInfo } from '../../models/FlowInfo';
import { Flows } from '../../resources/Flows';

export function FlowTooltip(props: ContentTooltipProps): JSX.Element {
  const { content, urls } = props;
  const flowInfo = content as FlowBaseInfo;
  const [draftLastEdited, setDraftLastEdited] = React.useState('');
  const [hideEditingLink, setHideEditingLink] = React.useState(false);
  React.useEffect(() => {
    const draftId = getFlowDraftId(flowInfo);
    if (flowInfo.draftStatus !== ContentDraftStatus.none && draftId) {
      Flows.getDraft(draftId.toString()).then((result) => {
        const lastUpdatedAt = (result as unknown as FlowDraftItemInfo).lastUpdatedAt;
        const lastUpdatedAtFormatted: string = Formatters.formatLongBestFitRelativeTime(new Date(lastUpdatedAt));
        setDraftLastEdited(Messages.draftLastEditIndicatorText({LAST_EDITED: lastUpdatedAtFormatted}));

        if (flowInfo.draftStatus === ContentDraftStatus.unpublished) {
          const server = getSessionManagementServiceInstance();
          if ((result as any).ownerId !== server.sessionUserId()) {
            setHideEditingLink(true);
          }
        }
      });
    }
  },
  // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [props.content.draftId]
  );

  const handleEditFlow = () => {
    if (flowInfo) {
      getAppStore().dispatch(editFlow(flowInfo));
    }
  };

  const projectLink = getProjectLink(flowInfo.project, urls);
  const personalSpaceLink = getPersonalSpaceLink(urls);
  const locationType = flowInfo.location?.type;
  const ownerId = content.owner?.id;

  return (
    <Tooltip>
      <Name name={content.name} />
      <MarkupField markup={props.content.description}
                  testId={TooltipDescriptionTestId}
                  extraClasses={[DetailsTooltipStyles.MetadataSectionWithContentDescription]} />
      <Tags tags={props.content.tags} />
      <BasicField name={Messages.inputSteps()} value={flowInfo.inputCount} />
      <BasicField name={Messages.outputSteps()} value={flowInfo.outputCount} />
      <LocationField inProjectSpace={props.inProjectSpace} inPersonalSpace={props.inPersonalSpace} locationType={locationType}
        projectLink={projectLink} personalSpaceLink={personalSpaceLink} contentOwnerId={ownerId}/>
      <User user={props.content.owner} label={Messages.owner()} urls={urls} />
      <Timestamp label={Messages.modified()} value={props.content.updatedAt} />
      {flowInfo.draftStatus !== ContentDraftStatus.none &&
        <TooltipDraftIndicator
          indicatorText={() => draftLastEdited}
          linkText={Messages.continueEditing}
          handleAction={handleEditFlow}
          hideEditingLink={hideEditingLink}
        />}
    </Tooltip>
  );
}
